import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { axiosInstance as axios } from '../context/AuthContext';
import { 
  Trophy, ChevronLeft, Lock, CheckCircle, 
  Home, BookOpen
} from 'lucide-react';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

const CATEGORY_INFO = {
  milestones: { label: 'Milestones', icon: Home, color: 'text-blue-500' },
  legacy: { label: 'Legacy', icon: BookOpen, color: 'text-purple-500' },
};

const TIER_STYLES = {
  bronze: 'from-amber-600 to-amber-800 border-amber-500',
  silver: 'from-gray-300 to-gray-500 border-gray-400',
  gold: 'from-yellow-400 to-yellow-600 border-yellow-500',
};

export default function Achievements() {
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPoints, setTotalPoints] = useState(0);
  const [earnedCount, setEarnedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    fetchAchievements();
  }, []);

  const fetchAchievements = async () => {
    try {
      const res = await axios.get(`${API}/achievements`);
      setAchievements(res.data.achievements || []);
      setTotalPoints(res.data.total_points || 0);
      setEarnedCount(res.data.earned_count || 0);
      setTotalCount(res.data.total_count || 0);
    } catch (err) {
      console.error('Failed to fetch achievements:', err);
    } finally {
      setLoading(false);
    }
  };

  const earnedAchievements = achievements.filter(a => a.earned);
  const lockedAchievements = achievements.filter(a => !a.earned);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/dashboard" className="text-gray-500 hover:text-gray-700">
            <ChevronLeft className="w-6 h-6" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
              <Trophy className="w-7 h-7 text-yellow-500" />
              Family Milestones
            </h1>
            <p className="text-gray-600 text-sm">Celebrate your family's journey together</p>
          </div>
        </div>

        {/* Progress Summary */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-3xl font-bold text-gray-800">{earnedCount} <span className="text-lg font-normal text-gray-500">of {totalCount}</span></p>
              <p className="text-gray-600">milestones achieved</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-yellow-500">{totalPoints}</p>
              <p className="text-gray-600 text-sm">points earned</p>
            </div>
          </div>
          {/* Progress bar */}
          <div className="mt-4">
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full transition-all duration-500"
                style={{ width: `${totalCount > 0 ? (earnedCount / totalCount) * 100 : 0}%` }}
              />
            </div>
          </div>
        </div>

        {/* Earned Milestones */}
        {earnedAchievements.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              Achieved
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {earnedAchievements.map((achievement) => (
                <MilestoneCard key={achievement.achievement_id} achievement={achievement} earned />
              ))}
            </div>
          </div>
        )}

        {/* Upcoming Milestones */}
        {lockedAchievements.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Lock className="w-5 h-5 text-gray-400" />
              Upcoming
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {lockedAchievements.map((achievement) => (
                <MilestoneCard key={achievement.achievement_id} achievement={achievement} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MilestoneCard({ achievement, earned = false }) {
  const tierStyle = TIER_STYLES[achievement.tier] || TIER_STYLES.bronze;
  const categoryInfo = CATEGORY_INFO[achievement.category] || CATEGORY_INFO.milestones;
  const CategoryIcon = categoryInfo.icon;
  
  return (
    <div className={`bg-white rounded-xl border ${earned ? 'border-green-200' : 'border-gray-200'} p-5 ${earned ? '' : 'opacity-70'}`}>
      <div className="flex items-start gap-4">
        <div className={`text-4xl ${earned ? '' : 'grayscale opacity-50'}`}>
          {achievement.icon}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <CategoryIcon className={`w-4 h-4 ${categoryInfo.color}`} />
            <span className={`text-xs px-2 py-0.5 rounded-full bg-gradient-to-r ${tierStyle} text-white capitalize`}>
              {achievement.tier}
            </span>
          </div>
          <h3 className="font-semibold text-gray-800">{achievement.name}</h3>
          <p className="text-sm text-gray-600 mt-1">{achievement.description}</p>
          
          {/* Progress Bar for locked */}
          {!earned && achievement.progress !== undefined && (
            <div className="mt-3">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>Progress</span>
                <span>{achievement.progress} / {achievement.requirement_value}</span>
              </div>
              <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-blue-500 rounded-full"
                  style={{ width: `${achievement.progress_percent}%` }}
                />
              </div>
            </div>
          )}
          
          {/* Earned date */}
          {earned && achievement.earned_at && (
            <p className="text-xs text-green-600 mt-2">
              Achieved {new Date(achievement.earned_at).toLocaleDateString()}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
